import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import expandIcon from '../../assets/images/expanded-indicator.svg';
import downArrowIcon from '../../assets/images/down-arrow-plain.svg';

import { useExpandableState } from './hooks/useExpandableState';
import { 
  getBaseClassName, 
  buildContainerClassName, 
  getArrowStyle, 
  createElementClassName,
  stopEventPropagation, 
} from './utils/expandableUtils';
import ExpandableIcon from './components/ExpandableIcon';
import ExpandableTitle from './components/ExpandableTitle';

import './ExpandableSection.scss';

const ExpandableSectionV2 = ({
  children,
  initiallyExpanded = false,
  expanded: controlledExpanded,
  name,
  onExpand,
  onClick,
  onClickData,
  onClear,
  isSubSection = false,
  useExpandIcon = false,
  id,
  hasBreakLine = false,
  icon,
  additionClass = '',
  animateExpand = false,
  hovered = false,
}) => {
  // Use custom hook for state management
  const { expanded, toggleExpanded } = useExpandableState({
    initiallyExpanded,
    expanded: controlledExpanded,
    animateExpand,
    onExpand,
  });

  // Memoized computed values
  const baseClassName = useMemo(() => getBaseClassName(isSubSection), [isSubSection]);
  
  const containerClassName = useMemo(() => 
    buildContainerClassName(baseClassName, expanded, hovered, additionClass),
    [baseClassName, expanded, hovered, additionClass]
  );

  const arrowStyle = useMemo(() => getArrowStyle(expanded), [expanded]);

  // Event handlers
  const handleExpandOrContract = useCallback((e) => {
    stopEventPropagation(e);
    toggleExpanded(!expanded);
  }, [expanded, toggleExpanded]);

  const handleTitleClick = useCallback((e) => {
    stopEventPropagation(e);
    
    if (onClick) {
      onClick(onClickData);
    } else if (onClear) {
      onClear();
    } else {
      toggleExpanded(!expanded);
    }
  }, [onClick, onClickData, onClear, expanded, toggleExpanded]);

  return (
    <div className={containerClassName} id={id}>
      {/* Left expand icon (when useExpandIcon is true and expanded) */}
      {useExpandIcon && expanded && (
        <ExpandableIcon
          baseClassName={baseClassName}
          position="left"
          src={expandIcon}
          alt="expanded"
          onClick={handleTitleClick}
        />
      )}

      {/* Title with or without icon */}
      <ExpandableTitle
        baseClassName={baseClassName}
        name={name}
        icon={icon}
        onClick={handleTitleClick}
      />

      {/* Break line */}
      {hasBreakLine && (
        <hr className={createElementClassName(baseClassName, 'break')} />
      )}

      {/* Right expand/collapse icon */}
      {!useExpandIcon && (
        <ExpandableIcon
          baseClassName={baseClassName}
          position="right"
          src={downArrowIcon}
          alt={expanded ? 'up arrow' : 'down arrow'}
          onClick={handleExpandOrContract}
          style={arrowStyle}
        />
      )}

      {/* Content */}
      <div className={createElementClassName(baseClassName, 'content')}>
        {children}
      </div>
    </div>
  );
};

ExpandableSectionV2.propTypes = {
  // Special react prop used to transclude child elements within this component
  children: PropTypes.any,
  // Initial state of the section - defaults to false (contracted) if none given
  initiallyExpanded: PropTypes.bool,
  // Controlled expanded state - if provided, this component becomes controlled
  expanded: PropTypes.bool,
  // Name to be displayed on the left of the section heading
  name: PropTypes.string,
  // Callback fired when expanded/contracted
  onExpand: PropTypes.func,
  // Click handler for the name/title
  onClick: PropTypes.func,
  // Data to pass to onClick handler
  onClickData: PropTypes.any,
  // Clear handler
  onClear: PropTypes.func,
  // If true, this is nested inside another <ExpandableSection>
  // currently only one level of nesting is supported
  isSubSection: PropTypes.bool,
  // If true, there will be an expand icon to the left instead of 
  // the default plus/minus icon to the right
  useExpandIcon: PropTypes.bool,
  // an optional id to be applied to the root <div>
  id: PropTypes.string,
  // an optional icon to display (expects a url)
  icon: PropTypes.string,
  // additional CSS class
  additionClass: PropTypes.string,
  // animate expand on mount
  animateExpand: PropTypes.bool,
  // hovered state
  hovered: PropTypes.bool,
  // break line
  hasBreakLine: PropTypes.bool,
};

export default ExpandableSectionV2;



// WEBPACK FOOTER //
// ./src/components/ExpandableSection/ExpandableSectionV2.js